import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import { rateLimitedApiCall } from "../utils/rateLimiter";
import axios from "axios";

interface AccountProfile {
  user_name?: string;
}

interface AccountData {
  profile?: AccountProfile;
  user_name?: string;
  availableBalance?: {
    cash?: number;
  } | null;
}

export interface Holding {
  tradingsymbol?: string;
  quantity?: number;
  average_price?: number;
  last_price?: number;
  pnl?: number;
}

interface AccountContextValue {
  account: AccountData | null;
  holdings: Holding[];
  loading: boolean;
  error: string | null;
  refreshData: (force?: boolean) => void;
  fetchAccountData: (forceRefresh?: boolean) => Promise<void>;
}

const AccountContext = createContext<AccountContextValue | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error("useAccount must be used within an AccountProvider");
  }
  return context;
};

export const AccountProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, authenticatedApiCall } = useAuth();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevAuth = useRef(false);

  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchAccountData = useCallback(
    async (forceRefresh = false) => {
      if (!isAuthenticated) {
        return;
      }

      const now = Date.now();

      if (!forceRefresh && isInitialized && now - lastFetchTime < CACHE_DURATION) {
        return;
      }

      if (!forceRefresh && now - lastFetchTime < 1000) {
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const accountRes = await rateLimitedApiCall(() =>
          authenticatedApiCall(() => axios.get("/api/trading/account")),
        );

        const holdingsRes = await rateLimitedApiCall(() =>
          authenticatedApiCall(() => axios.get("/api/trading/holdings")),
        );
        setAccount(accountRes.data.data);
        setHoldings(holdingsRes.data.data);
        setLastFetchTime(now);
        setIsInitialized(true);
      } catch (err) {
        const message = err instanceof Error ? err.message : "";
        const errorType = (err as any)?.response?.data?.error_type;
        if (
          !message.includes("Session expired") &&
          !message.includes("Incorrect `api_key` or `access_token`") &&
          errorType !== "TokenException"
        ) {
          setError("Failed to fetch account data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated],
  );

  useEffect(() => {
    if (isAuthenticated && !prevAuth.current) {
      const initialFetch = async () => {
        await fetchAccountData(true);
      };
      initialFetch();
    }
    prevAuth.current = isAuthenticated;
    if (!isAuthenticated) {
      setAccount(null);
      setHoldings([]);
      setIsInitialized(false);
      setLastFetchTime(0);
    }
  }, [isAuthenticated]);

  const refreshData = useCallback(
    (force = true) => {
      const now = Date.now();

      if (!force && now - lastFetchTime < 2000) {
        return;
      }
      fetchAccountData(force);
    },
    [fetchAccountData, lastFetchTime],
  );

  const value = {
    account,
    holdings,
    loading,
    error,
    refreshData,
    fetchAccountData,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};
